
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * StopOutNotify struct
 */
class StopOutNotify {
  /**
   * Initialize struct
   * @param {!number=} login
   * @param {!Date=} timestamp
   * @param {!number=} marginLevel
   * @constructor
   */
  constructor (login = new UInt64(0, 0), timestamp = new Date(0), marginLevel = 0.0) {
    this.Login = login
    this.Timestamp = timestamp
    this.MarginLevel = marginLevel
  }

  /**
   * Copy struct (shallow copy)
   * @this {!StopOutNotify}
   * @param {!StopOutNotify} other Other struct
   * @returns {!StopOutNotify} This struct
   */
  copy (other) {
    if (other.Login != null) {
      this.Login = UInt64.fromNumber(other.Login)
    } else {
      this.Login = null
    }
    if (other.Timestamp != null) {
      if (other.Timestamp instanceof Date) {
        this.Timestamp = new Date(other.Timestamp.getTime())
      } else {
        this.Timestamp = new Date(Math.round(other.Timestamp / 1000000))
      }
    } else {
      this.Timestamp = null
    }
    if (other.MarginLevel != null) {
      this.MarginLevel = other.MarginLevel
    } else {
      this.MarginLevel = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!StopOutNotify}
   * @returns {!StopOutNotify} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new StopOutNotifyModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new StopOutNotifyModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!StopOutNotify}
   * @param {!StopOutNotify} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof StopOutNotify)) {
      throw new TypeError('Instance of StopOutNotify is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!StopOutNotify}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      Login: ((this.Login != null) ? this.Login.toNumber() : null),
      Timestamp: ((this.Timestamp != null) ? (this.Timestamp.getTime() * 1000000) : null),
      MarginLevel: ((this.MarginLevel != null) ? this.MarginLevel : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return StopOutNotify.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!StopOutNotify} other Object value
   * @returns {!StopOutNotify} Created struct
   */
  static fromObject (other) {
    return new StopOutNotify().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!StopOutNotify}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return StopOutNotify.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!StopOutNotify}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6207
  }
}

export { StopOutNotify };

/**
 * Fast Binary Encoding StopOutNotify field model
 */
class FieldModelStopOutNotify extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.Login = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.Timestamp = new fbe.FieldModelTimestamp(buffer, this.Login.fbeOffset + this.Login.fbeSize)
    this.MarginLevel = new fbe.FieldModelDouble(buffer, this.Timestamp.fbeOffset + this.Timestamp.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelStopOutNotify}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelStopOutNotify}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.Login.fbeSize + this.Timestamp.fbeSize + this.MarginLevel.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelStopOutNotify}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.Login.fbeExtra + this.Timestamp.fbeExtra + this.MarginLevel.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelStopOutNotify}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelStopOutNotify.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelStopOutNotify}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6207
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelStopOutNotify}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelStopOutNotify}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Login.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Timestamp.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Timestamp.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timestamp.fbeSize

    if ((fbeCurrentSize + this.MarginLevel.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarginLevel.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginLevel.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelStopOutNotify}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelStopOutNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelStopOutNotify}
   * @param {!StopOutNotify} fbeValue Default value, defaults is new StopOutNotify()
   * @returns {!StopOutNotify} StopOutNotify value
   */
  get (fbeValue = new StopOutNotify()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelStopOutNotify}
   * @param {!StopOutNotify} fbeValue StopOutNotify value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.Login.fbeSize) <= fbeStructSize) {
      fbeValue.Login = this.Login.get()
    } else {
      fbeValue.Login = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Login.fbeSize

    if ((fbeCurrentSize + this.Timestamp.fbeSize) <= fbeStructSize) {
      fbeValue.Timestamp = this.Timestamp.get()
    } else {
      fbeValue.Timestamp = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Timestamp.fbeSize

    if ((fbeCurrentSize + this.MarginLevel.fbeSize) <= fbeStructSize) {
      fbeValue.MarginLevel = this.MarginLevel.get()
    } else {
      fbeValue.MarginLevel = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginLevel.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelStopOutNotify}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelStopOutNotify}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelStopOutNotify}
   * @param {!StopOutNotify} fbeValue StopOutNotify value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelStopOutNotify}
   * @param {StopOutNotify} fbeValue StopOutNotify value
   */
  setFields (fbeValue) {
    this.Login.set(fbeValue.Login)
    this.Timestamp.set(fbeValue.Timestamp)
    this.MarginLevel.set(fbeValue.MarginLevel)
  }
}

export { FieldModelStopOutNotify };

/**
 * Fast Binary Encoding StopOutNotify model
 */
class StopOutNotifyModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelStopOutNotify(this.buffer, 4)
  }

  /**
   * Get the StopOutNotify model
   * @this {!StopOutNotifyModel}
   * @returns {!FieldModelStopOutNotify} model StopOutNotify model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!StopOutNotifyModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!StopOutNotifyModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return StopOutNotifyModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!StopOutNotifyModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelStopOutNotify.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!StopOutNotifyModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!StopOutNotifyModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!StopOutNotifyModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!StopOutNotifyModel}
   * @param {!StopOutNotify} value StopOutNotify value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!StopOutNotifyModel}
   * @param {!StopOutNotify} value StopOutNotify value, defaults is new StopOutNotify()
   * @return {!object} Deserialized StopOutNotify value and its size
   */
  deserialize (value = new StopOutNotify()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new StopOutNotify(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new StopOutNotify(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!StopOutNotifyModel}
   * @param {!number} prev Previous StopOutNotify model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { StopOutNotifyModel };
